// Agency multi-drop batch order builder.
//
// Two modes:
//   • same       — pick items ONCE, then attach N drop-offs
//   • different  — for each drop-off, pick its own items
//
// Submits to POST /api/agency/batch which creates one PO order per drop
// under a single `batch_id`. Admin sees them grouped in the Batches tab.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  MapPin,
  CalendarDays,
  Package,
  Layers,
  Copy,
  CheckCircle2,
  Loader2,
  Search,
  X,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import BackToTop from "./BackToTop";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api, formatApiErrorDetail } from "../lib/api";

const emptyDrop = (idx) => ({
  _tempId: `drop-${idx}-${Math.random().toString(36).slice(2, 8)}`,
  items: [], // used only in "different" mode
  customer_name: "",
  customer_phone: "",
  customer_email: "",
  address: "",
  address_line2: "",
  city: "",
  state: "NY",
  zip_code: "",
  event_date: "",
  event_time: "",
  on_site_contact_name: "",
  on_site_contact_phone: "",
  delivery_instructions: "",
  delivery_fee: "",
  po_number: "",
  notes: "",
});

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Cheap flat picker used inside both the shared-items area and each
// per-drop editor. Keeps UI density down vs. importing the OptionPickerModal.
const ItemPicker = ({ menu, items, onAdd, onRemove, onQty, testIdPrefix = "batch-item" }) => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return menu.items.slice(0, 60);
    return menu.items.filter(
      (m) =>
        m.name.toLowerCase().includes(needle) ||
        (m.category || "").toLowerCase().includes(needle),
    );
  }, [menu.items, q]);
  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-charcoal/40" />
        <input
          data-testid={`${testIdPrefix}-search`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search menu…"
          className="h-10 w-full rounded-xl bg-brand-cream/50 border border-black/10 pl-10 pr-3 text-[13px] text-brand-charcoal"
        />
      </div>
      <div className="rounded-xl border border-black/10 divide-y divide-black/5 max-h-56 overflow-y-auto bg-white">
        {filtered.length === 0 ? (
          <p className="p-4 text-[12px] text-brand-charcoal/50 italic">
            No matches
          </p>
        ) : (
          filtered.map((m) => (
            <button
              key={m.id}
              type="button"
              data-testid={`${testIdPrefix}-add-${m.id}`}
              onClick={() => onAdd(m)}
              className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-brand-cream/40 transition-colors"
            >
              <span className="flex-1 min-w-0">
                <span className="block truncate text-[13px] font-bold text-brand-charcoal">
                  {m.name}
                </span>
                <span className="block text-[11px] text-brand-charcoal/55">
                  {m.category || "—"} · {money(m.price)}
                </span>
              </span>
              <Plus className="h-4 w-4 text-brand-teal shrink-0" />
            </button>
          ))
        )}
      </div>
      {items.length > 0 && (
        <ul
          data-testid={`${testIdPrefix}-selected`}
          className="rounded-xl border border-black/10 divide-y divide-black/5 bg-white"
        >
          {items.map((li, idx) => (
            <li
              key={`${li.package_id}-${idx}`}
              className="flex items-center gap-2 px-3 py-2"
            >
              <span className="flex-1 min-w-0 text-[13px] text-brand-charcoal truncate">
                {li.name || li.package_id}
              </span>
              <input
                data-testid={`${testIdPrefix}-qty-${idx}`}
                type="number"
                min="1"
                max="200"
                value={li.quantity}
                onChange={(e) => onQty(idx, e.target.value)}
                className="h-8 w-16 rounded-lg bg-brand-cream/50 border border-black/10 px-2 text-[12.5px] text-right tabular-nums"
              />
              <button
                type="button"
                data-testid={`${testIdPrefix}-remove-${idx}`}
                onClick={() => onRemove(idx)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-brand-charcoal/50 hover:text-red-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AgencyBatchPage = () => {
  const { user } = useAuth();
  const { menu, items: cartItems, clear } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "different" ? "different" : "same";

  const [batchName, setBatchName] = useState("");
  const [mode, setMode] = useState(initialMode);
  const [sharedItems, setSharedItems] = useState([]);
  const [drops, setDrops] = useState(() => [emptyDrop(0)]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // On first mount, if the user came from /cart with items already in it,
  // seed the shared-items list so they don't have to pick again. Resolve
  // display names against the menu catalog so pre-populated rows read
  // "Signature Sauced Wings" and not the raw slug.
  useEffect(() => {
    if (sharedItems.length === 0 && cartItems && cartItems.length > 0) {
      const catalog = menu?.items || [];
      setSharedItems(
        cartItems.map((it) => {
          const found = catalog.find((m) => m.id === it.package_id);
          return {
            package_id: it.package_id,
            quantity: it.quantity,
            name: it.name || found?.name || it.package_id,
            selected_options: it.selected_options,
          };
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu?.items?.length]);

  // Guard: agency-only. We render a soft prompt rather than 404 to help users.
  if (user === null) return null;
  if (!user || (user.role !== "agency" && user.role !== "admin")) {
    return (
      <div className="App font-sans bg-white text-brand-charcoal antialiased">
        <Header />
        <main className="pt-28 pb-24 min-h-[60vh] bg-brand-cream flex items-center">
          <div className="max-w-lg mx-auto text-center px-6" data-testid="batch-agency-required">
            <h1 className="font-black text-3xl tracking-tight">Agency account required</h1>
            <p className="mt-3 text-[14px] text-brand-charcoal/70">
              Multi-drop batch ordering is available to approved agency accounts.
              Sign in with your agency account, or apply for access.
            </p>
            <Link
              to="/sign-in"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
            >
              Sign in
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const addSharedItem = (m) =>
    setSharedItems((arr) => {
      const existing = arr.findIndex((x) => x.package_id === m.id);
      if (existing !== -1) {
        const next = arr.slice();
        next[existing] = { ...next[existing], quantity: (next[existing].quantity || 1) + 1 };
        return next;
      }
      return [...arr, { package_id: m.id, quantity: 1, name: m.name }];
    });
  const removeSharedItem = (idx) => setSharedItems((arr) => arr.filter((_, i) => i !== idx));
  const setSharedQty = (idx, v) =>
    setSharedItems((arr) => arr.map((x, i) => (i === idx ? { ...x, quantity: Math.max(1, parseInt(v || "1", 10)) } : x)));

  const updateDrop = (i, patch) => setDrops((arr) => arr.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const addDropAt = () => setDrops((arr) => [...arr, emptyDrop(arr.length)]);
  const duplicateDrop = (i) =>
    setDrops((arr) => {
      const src = arr[i];
      const dup = { ...src, _tempId: `drop-${arr.length}-${Math.random().toString(36).slice(2, 8)}`, po_number: "", event_date: "" };
      return [...arr.slice(0, i + 1), dup, ...arr.slice(i + 1)];
    });
  const removeDrop = (i) => setDrops((arr) => (arr.length === 1 ? arr : arr.filter((_, idx) => idx !== i)));
  const addItemToDrop = (i, m) =>
    updateDrop(i, {
      items: (() => {
        const cur = drops[i].items || [];
        const ex = cur.findIndex((x) => x.package_id === m.id);
        if (ex !== -1) {
          const nxt = cur.slice();
          nxt[ex] = { ...nxt[ex], quantity: (nxt[ex].quantity || 1) + 1 };
          return nxt;
        }
        return [...cur, { package_id: m.id, quantity: 1, name: m.name }];
      })(),
    });
  const removeItemFromDrop = (i, idx) =>
    updateDrop(i, { items: (drops[i].items || []).filter((_, k) => k !== idx) });
  const setDropQty = (i, idx, v) =>
    updateDrop(i, {
      items: (drops[i].items || []).map((x, k) => (k === idx ? { ...x, quantity: Math.max(1, parseInt(v || "1", 10)) } : x)),
    });

  const validate = () => {
    if (mode === "same" && sharedItems.length === 0)
      return "Add at least one item to the shared items list.";
    for (let i = 0; i < drops.length; i++) {
      const d = drops[i];
      if (!d.customer_name.trim()) return `Drop #${i + 1}: recipient name required.`;
      if (!d.customer_phone.trim()) return `Drop #${i + 1}: recipient phone required.`;
      if (!d.address.trim()) return `Drop #${i + 1}: address required.`;
      if (!d.city.trim()) return `Drop #${i + 1}: city required.`;
      if (!d.zip_code.trim()) return `Drop #${i + 1}: ZIP required.`;
      if (!d.event_date.trim()) return `Drop #${i + 1}: event date required.`;
      if (!d.po_number.trim()) return `Drop #${i + 1}: PO number required.`;
      if (mode === "different" && (!d.items || d.items.length === 0))
        return `Drop #${i + 1}: at least one item required.`;
    }
    return "";
  };

  const dropEstimate = (d) => {
    const cat = menu.items || [];
    const lines = mode === "same" ? sharedItems : d.items || [];
    let sub = 0;
    for (const li of lines) {
      const m = cat.find((x) => x.id === li.package_id);
      if (!m) continue;
      sub += (m.price || 0) * (li.quantity || 1);
    }
    return sub + Number(d.delivery_fee || 0);
  };
  const batchTotal = drops.reduce((acc, d) => acc + dropEstimate(d), 0);

  const onSubmit = async () => {
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: batchName.trim() || null,
        mode,
        items:
          mode === "same"
            ? sharedItems.map((it) => ({ package_id: it.package_id, quantity: it.quantity }))
            : null,
        drops: drops.map((d) => ({
          items:
            mode === "different"
              ? (d.items || []).map((it) => ({ package_id: it.package_id, quantity: it.quantity }))
              : null,
          customer_name: d.customer_name,
          customer_phone: d.customer_phone,
          customer_email: d.customer_email || null,
          address: d.address,
          address_line2: d.address_line2 || null,
          city: d.city,
          state: d.state,
          zip_code: d.zip_code,
          event_date: d.event_date,
          event_time: d.event_time || null,
          on_site_contact_name: d.on_site_contact_name || null,
          on_site_contact_phone: d.on_site_contact_phone || null,
          delivery_instructions: d.delivery_instructions || null,
          delivery_fee: Number(d.delivery_fee || 0),
          po_number: d.po_number,
          notes: d.notes || null,
        })),
      };
      const { data } = await api.post("/agency/batch", payload);
      // Clear the source cart if the user pre-filled from it, then navigate.
      if (cartItems && cartItems.length > 0) clear();
      navigate(`/agency/batch/received?ref=${encodeURIComponent(data.ref)}&drops=${data.drops_count}&total=${data.total}`);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App font-sans bg-white text-brand-charcoal antialiased">
      <Header />
      <main className="pt-24 pb-16 bg-brand-cream min-h-[80vh]">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-gold/20 text-brand-gold-dark px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em]">
                <Layers className="h-3.5 w-3.5" /> Agency batch order
              </div>
              <h1 className="mt-3 font-display font-black text-3xl sm:text-4xl tracking-tight text-brand-charcoal">
                Multi-drop batch order
              </h1>
              <p className="mt-2 text-[13.5px] text-brand-charcoal/65 max-w-2xl leading-relaxed">
                Create up to 25 linked PO invoices under your account in one submission —
                perfect for multiple schools, sites, or dates in one week. Each drop-off
                gets its own PO number, delivery fee, address, and time.
              </p>
            </div>
            <Link
              to="/cart"
              className="inline-flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/70 hover:text-brand-teal transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Back to cart
            </Link>
          </div>

          {/* Batch header */}
          <section className="mt-6 rounded-2xl bg-white border border-black/5 p-5 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5">
                <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                  Batch label (optional)
                </span>
                <input
                  data-testid="batch-name"
                  value={batchName}
                  onChange={(e) => setBatchName(e.target.value)}
                  placeholder="e.g. P469X Bronx · Feb 15 week"
                  className="h-10 rounded-xl bg-brand-cream/50 border border-black/10 px-3 text-[13px] text-brand-charcoal"
                />
              </label>
              <div className="flex flex-col gap-1.5">
                <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                  Items mode
                </span>
                <div className="flex items-center gap-2">
                  {[
                    { key: "same", label: "Same items to each location" },
                    { key: "different", label: "Different items per location" },
                  ].map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      data-testid={`batch-mode-${m.key}`}
                      onClick={() => setMode(m.key)}
                      className={`flex-1 h-10 rounded-xl border text-[12px] font-bold uppercase tracking-[0.08em] transition-colors ${
                        mode === m.key
                          ? "bg-brand-teal text-white border-brand-teal"
                          : "bg-white text-brand-charcoal/70 border-black/10 hover:text-brand-charcoal"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Shared items (only in "same" mode) */}
          {mode === "same" && (
            <section
              data-testid="batch-shared-items-section"
              className="mt-4 rounded-2xl bg-white border border-black/5 p-5 sm:p-6"
            >
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-brand-teal" />
                <h2 className="text-[13px] font-black uppercase tracking-[0.14em] text-brand-charcoal">
                  Shared items · applied to every drop
                </h2>
              </div>
              <p className="mt-1 text-[12px] text-brand-charcoal/55">
                Every drop-off receives this same list of items.
              </p>
              <div className="mt-3">
                <ItemPicker
                  menu={menu}
                  items={sharedItems}
                  onAdd={addSharedItem}
                  onRemove={removeSharedItem}
                  onQty={setSharedQty}
                  testIdPrefix="batch-shared"
                />
              </div>
            </section>
          )}

          {/* Drops list */}
          <section className="mt-4 space-y-4">
            {drops.map((d, i) => (
              <article
                key={d._tempId}
                data-testid={`batch-drop-${i}`}
                className="rounded-2xl bg-white border border-black/5 p-5 sm:p-6"
              >
                <header className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-brand-charcoal text-white font-black text-[13px]">
                      {i + 1}
                    </span>
                    <h3 className="text-[14px] font-black text-brand-charcoal">
                      Drop-off {i + 1}
                    </h3>
                    <span className="text-[11.5px] text-brand-charcoal/60 tabular-nums">
                      est. {money(dropEstimate(d))}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      data-testid={`batch-drop-${i}-duplicate`}
                      onClick={() => duplicateDrop(i)}
                      title="Duplicate this drop"
                      className="inline-flex items-center gap-1 rounded-full bg-white border border-black/10 text-brand-charcoal/70 px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:text-brand-teal hover:border-brand-teal transition-colors"
                    >
                      <Copy className="h-3.5 w-3.5" /> Duplicate
                    </button>
                    {drops.length > 1 && (
                      <button
                        type="button"
                        data-testid={`batch-drop-${i}-remove`}
                        onClick={() => removeDrop(i)}
                        title="Remove this drop"
                        className="inline-flex items-center gap-1 rounded-full bg-white border border-red-100 text-red-500 px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </button>
                    )}
                  </div>
                </header>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <DField label="Recipient name" testId={`batch-drop-${i}-name`}>
                    <input
                      value={d.customer_name}
                      onChange={(e) => updateDrop(i, { customer_name: e.target.value })}
                      className={inputCls}
                      data-testid={`batch-drop-${i}-name-input`}
                    />
                  </DField>
                  <DField label="Recipient phone">
                    <input
                      value={d.customer_phone}
                      onChange={(e) => updateDrop(i, { customer_phone: e.target.value })}
                      className={inputCls}
                      data-testid={`batch-drop-${i}-phone-input`}
                    />
                  </DField>
                  <DField label="Street address" span="sm:col-span-2">
                    <input
                      value={d.address}
                      onChange={(e) => updateDrop(i, { address: e.target.value })}
                      className={inputCls}
                      data-testid={`batch-drop-${i}-address-input`}
                    />
                  </DField>
                  <DField label="City">
                    <input
                      value={d.city}
                      onChange={(e) => updateDrop(i, { city: e.target.value })}
                      className={inputCls}
                      data-testid={`batch-drop-${i}-city-input`}
                    />
                  </DField>
                  <DField label="State / ZIP">
                    <div className="flex gap-2">
                      <input
                        value={d.state}
                        onChange={(e) => updateDrop(i, { state: e.target.value })}
                        className={`${inputCls} w-16`}
                        data-testid={`batch-drop-${i}-state-input`}
                      />
                      <input
                        value={d.zip_code}
                        onChange={(e) => updateDrop(i, { zip_code: e.target.value })}
                        className={`${inputCls} flex-1`}
                        data-testid={`batch-drop-${i}-zip-input`}
                      />
                    </div>
                  </DField>
                  <DField label="Event date">
                    <input
                      type="date"
                      value={d.event_date}
                      onChange={(e) => updateDrop(i, { event_date: e.target.value })}
                      className={inputCls}
                      data-testid={`batch-drop-${i}-date-input`}
                    />
                  </DField>
                  <DField label="Event time">
                    <input
                      type="time"
                      value={d.event_time}
                      onChange={(e) => updateDrop(i, { event_time: e.target.value })}
                      className={inputCls}
                      data-testid={`batch-drop-${i}-time-input`}
                    />
                  </DField>
                  <DField label="On-site contact">
                    <input
                      value={d.on_site_contact_name}
                      onChange={(e) => updateDrop(i, { on_site_contact_name: e.target.value })}
                      className={inputCls}
                      data-testid={`batch-drop-${i}-onsite-name-input`}
                    />
                  </DField>
                  <DField label="On-site phone">
                    <input
                      value={d.on_site_contact_phone}
                      onChange={(e) => updateDrop(i, { on_site_contact_phone: e.target.value })}
                      className={inputCls}
                      data-testid={`batch-drop-${i}-onsite-phone-input`}
                    />
                  </DField>
                  <DField label="PO number">
                    <input
                      value={d.po_number}
                      onChange={(e) => updateDrop(i, { po_number: e.target.value })}
                      placeholder="e.g. WO270000108"
                      className={inputCls}
                      data-testid={`batch-drop-${i}-po-input`}
                    />
                  </DField>
                  <DField label="Delivery fee ($)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={d.delivery_fee}
                      onChange={(e) => updateDrop(i, { delivery_fee: e.target.value })}
                      className={`${inputCls} tabular-nums`}
                      data-testid={`batch-drop-${i}-fee-input`}
                    />
                  </DField>
                  <DField label="Delivery access notes" span="sm:col-span-2">
                    <textarea
                      value={d.delivery_instructions}
                      onChange={(e) => updateDrop(i, { delivery_instructions: e.target.value })}
                      rows={2}
                      maxLength={1000}
                      placeholder="Loading dock, ramp, floor, buzzer, etc."
                      className={`${inputCls} min-h-[64px]`}
                      data-testid={`batch-drop-${i}-notes-input`}
                    />
                  </DField>
                </div>

                {mode === "different" && (
                  <div className="mt-5 pt-5 border-t border-black/5">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-brand-teal" />
                      <h4 className="text-[12px] font-black uppercase tracking-[0.14em] text-brand-charcoal/75">
                        Items for this drop
                      </h4>
                    </div>
                    <div className="mt-3">
                      <ItemPicker
                        menu={menu}
                        items={d.items || []}
                        onAdd={(m) => addItemToDrop(i, m)}
                        onRemove={(idx) => removeItemFromDrop(i, idx)}
                        onQty={(idx, v) => setDropQty(i, idx, v)}
                        testIdPrefix={`batch-drop-${i}-items`}
                      />
                    </div>
                  </div>
                )}
              </article>
            ))}
            <button
              type="button"
              data-testid="batch-add-drop"
              onClick={addDropAt}
              className="w-full rounded-2xl border-2 border-dashed border-brand-charcoal/20 bg-white/40 py-4 text-[12px] font-black uppercase tracking-[0.14em] text-brand-charcoal/70 hover:text-brand-teal hover:border-brand-teal transition-colors inline-flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" /> Add another drop-off
            </button>
          </section>

          {/* Sticky footer with total + submit */}
          <div className="sticky bottom-4 mt-6 rounded-2xl bg-brand-charcoal text-white p-4 sm:p-5 shadow-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/60">
                Estimated batch total
              </span>
              <span
                data-testid="batch-total-estimate"
                className="font-black text-2xl tabular-nums"
              >
                {money(batchTotal)}
              </span>
              <span className="text-[11.5px] text-white/50">
                · {drops.length} drop{drops.length === 1 ? "" : "s"}
              </span>
            </div>
            {error && (
              <p
                data-testid="batch-error"
                className="text-[12px] font-bold text-red-300"
              >
                {error}
              </p>
            )}
            <button
              type="button"
              data-testid="batch-submit"
              onClick={onSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-charcoal px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-white transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              {submitting ? "Submitting…" : "Submit batch"}
            </button>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

const inputCls =
  "h-10 rounded-xl bg-brand-cream/50 border border-black/10 px-3 text-[13px] text-brand-charcoal placeholder:text-brand-charcoal/40";

const DField = ({ label, children, span = "", testId }) => (
  <label className={`flex flex-col gap-1.5 ${span}`} data-testid={testId || undefined}>
    <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
      {label}
    </span>
    {children}
  </label>
);

export default AgencyBatchPage;
